import {
  AccessTime as TimeIcon,
  AddCircleOutline as IncludeInReportsIcon,
  AdjustOutlined as IncludedInReportsStatusIcon,
  Battery0Bar as Battery0Icon,
  Battery20 as Battery20Icon,
  Battery50 as Battery50Icon,
  Battery80 as Battery80Icon,
  BatteryFull as BatteryFullIcon,
  CheckCircleOutlined as EnabledIcon,
  Dangerous as DisableIcon,
  DangerousOutlined as DisabledStatusIcon,
  HideSource as ExcludedFromReportsStatusIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Memory as SystemIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  PhoneIphone as DeviceIcon,
  PhonelinkLock,
  PinOutlined as PinIcon,
  Power as PowerIcon,
  Refresh as RestartIcon,
  RemoveCircleOutline as ExcludeFromReportsIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  Terminal as LogsIcon,
  Videocam as CameraIcon,
  VideocamOff as CameraOffIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  GridLegacy as Grid,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  AdminDialog,
  AdminEmptyState,
  PageShell,
  AdminSectionPanel,
  ChartHoverTooltip,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { useAuth } from "@/contexts/AuthContext";
import { clearCommandCenterSessionCache } from "@/pages/admin/command-center/commandCenterSessionCache";
import { CoreService } from "@/services/core/CoreService";
import {
  buildDeviceLogsUrl,
  Device,
  type DeviceMetadataSnapshot,
  DeviceService,
} from "@/services/features/DeviceService";
import { Store, StoreService } from "@/services/features/StoreService";
import {
  type DeviceBatteryColor,
  type DeviceBatteryIcon,
  getDeviceBatteryDisplay,
} from "@/utils/deviceBattery";
import { getCurrentRegion } from "@/utils/environment";
import { formatMetadataKey } from "@/utils/metadata";
import { formatPercentFromWhole } from "@/utils/number";
import { formatStoreSelectorLabel } from "@/utils/store";
import {
  formatRelativeStoreTime,
  formatStoreDateTimeWithTimezone,
  getBrowserTimezone,
} from "@/utils/storeTime";

interface DeviceHistoryParams {
  [key: string]: string | undefined;
  deviceId: string;
}

interface PingChartPoint {
  timestamp: number;
  status: number;
}

interface PingChartTooltipProps {
  active?: boolean;
  coordinate?: { x: number; y: number };
  payload?: Array<{ value?: number | string; payload?: PingChartPoint }>;
}

const DeviceHistory: React.FC = () => {
  const { deviceId } = useParams<DeviceHistoryParams>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const browserTimezone = getBrowserTimezone();
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";
  const pingChartContainerRef = React.useRef<HTMLDivElement>(null);
  const deviceService = useMemo(() => DeviceService.getInstance(), []);
  const storeService = useMemo(() => StoreService.getInstance(), []);
  const coreService = useMemo(() => CoreService.getInstance(), []);

  // State
  const [device, setDevice] = useState<Device | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [areaName, setAreaName] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [pings, setPings] = useState<Device[]>([]);
  const [latestMetadata, setLatestMetadata] = useState<DeviceMetadataSnapshot | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeFilterAnchor, setTimeFilterAnchor] = useState<null | HTMLElement>(null);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const effectiveTimezone = store?.timezone || browserTimezone;
  const batteryDisplay = device ? getDeviceBatteryDisplay(device) : null;

  const loadDeviceData = useCallback(async () => {
    if (!deviceId) {
      setIsHistoryLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setDevice(null);
      setStore(null);
      setAreaName(null);
      setRoomName(null);

      // Load device details
      const deviceResponse = await deviceService.getDeviceById(parseInt(deviceId));
      if (!deviceResponse.success || !deviceResponse.body) {
        throw new Error(t("management.devices.deviceHistory.errors.failedLoadDeviceDetails"));
      }

      const deviceData = deviceResponse.body;

      // Load store details
      const storeResponse = await storeService.getStoreById(deviceData.storeId);
      if (storeResponse.data) {
        setStore(storeResponse.data);
      }

      // Load changing room details if applicable
      if (deviceData.changingRoomId) {
        try {
          const roomsResponse = await storeService.getChangingRoomsByStore(deviceData.storeId);
          if (roomsResponse.success && roomsResponse.data) {
            for (const group of roomsResponse.data) {
              if (group.changingRooms && group.changingRoomGroup) {
                const room = group.changingRooms.find((r) => r.id === deviceData.changingRoomId);
                if (room) {
                  setAreaName(group.changingRoomGroup.name);
                  setRoomName(room.name);
                  break;
                }
              }
            }
          }
        } catch {}
      }

      setDevice(deviceData);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.devices.deviceHistory.errors.errorLoadingDeviceData"),
      );
      setIsHistoryLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [deviceId, deviceService, storeService]);

  const loadDeviceHistory = useCallback(
    async (deviceId: number) => {
      try {
        setIsHistoryLoading(true);
        setError(null);
        setPings([]);

        const response = await deviceService.getDeviceHistoryById(deviceId, days);

        if (!response.success) {
          throw new Error(
            typeof response.error === "string"
              ? response.error
              : t("management.devices.deviceHistory.errors.failedLoadDeviceHistory"),
          );
        }

        if (!response.body || !Array.isArray(response.body)) {
          throw new Error(t("management.devices.deviceHistory.errors.invalidHistoryData"));
        }

        setPings(response.body);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("management.devices.deviceHistory.errors.errorLoadingDeviceHistory"),
        );
        setPings([]);
      } finally {
        setIsHistoryLoading(false);
      }
    },
    [days, deviceService],
  );

  const loadLatestMetadata = useCallback(
    async (deviceId: number, days: number) => {
      setIsMetadataLoading(true);
      setLatestMetadata(null);
      try {
        const response = await deviceService.getLatestDeviceMetadataById(deviceId, days);
        if (response.success && response.body && Object.keys(response.body.metadata).length > 0) {
          setLatestMetadata(response.body);
        }
      } finally {
        setIsMetadataLoading(false);
      }
    },
    [deviceService],
  );

  useEffect(() => {
    document.title = t("management.devices.deviceHistory.documentTitle", { deviceId });
    void loadDeviceData();
  }, [deviceId, loadDeviceData]);

  // Separate useEffect for loading history to ensure device is loaded first
  useEffect(() => {
    if (device?.id && days) {
      void loadDeviceHistory(device.id);
    }
  }, [device?.id, days, loadDeviceHistory]);

  useEffect(() => {
    if (device?.id) {
      void loadLatestMetadata(device.id, days);
    }
  }, [device?.id, days, loadLatestMetadata]);

  const chartData = useMemo<PingChartPoint[]>(() => {
    const timezone = effectiveTimezone;
    const startDate = moment().subtract(days, "days").tz(timezone);
    const endDate = moment().tz(timezone);
    const realPings = pings.filter((ping): ping is Device & { updatedAt: string } =>
      Boolean(ping.updatedAt),
    );

    if (realPings.length === 0) return [];

    const allPings = [...realPings, { updatedAt: startDate.toISOString() }].filter(
      (ping): ping is Device & { updatedAt: string } => Boolean(ping.updatedAt),
    );

    // Sort pings by timestamp
    const sortedPings = allPings.sort(
      (a, b) => moment(a.updatedAt).valueOf() - moment(b.updatedAt).valueOf(),
    );

    const points: PingChartPoint[] = [];

    // Process each ping
    for (let i = 0; i < sortedPings.length; i++) {
      const currentPing = moment(sortedPings[i].updatedAt).tz(timezone);
      points.push({
        timestamp: currentPing.valueOf(),
        status: 1,
      });

      // If there's a next ping and the gap is more than 10 minutes
      if (sortedPings[i + 1]) {
        const nextPing = moment(sortedPings[i + 1].updatedAt).tz(timezone);
        if (nextPing.diff(currentPing, "minutes") > 10) {
          // Add offline point 5 minutes after current ping
          points.push({
            timestamp: currentPing.clone().add(5, "minutes").valueOf(),
            status: 0,
          });

          // Add offline point 5 minutes before next ping
          points.push({
            timestamp: nextPing.clone().subtract(5, "minutes").valueOf(),
            status: 0,
          });
        }
      }
    }

    const lastRealPing = [...realPings].sort(
      (a, b) => moment(b.updatedAt).valueOf() - moment(a.updatedAt).valueOf(),
    )[0];

    if (lastRealPing) {
      const lastPingTime = moment(lastRealPing.updatedAt).tz(timezone);
      const isOfflineAtEnd = endDate.diff(lastPingTime, "minutes") > 10;

      if (isOfflineAtEnd) {
        points.push({
          timestamp: lastPingTime.clone().add(5, "minutes").valueOf(),
          status: 0,
        });
      }

      points.push({ timestamp: endDate.valueOf(), status: isOfflineAtEnd ? 0 : 1 });
    }

    return points;
  }, [days, effectiveTimezone, pings]);

  const xAxisTicks = useMemo(() => {
    if (chartData.length === 0) {
      return [];
    }

    const timestamps = chartData.map(({ timestamp }) => timestamp);
    const firstTimestamp = Math.min(...timestamps);
    const lastTimestamp = Math.max(...timestamps);
    if (days === 1) {
      const hour = moment(firstTimestamp).tz(effectiveTimezone).startOf("hour");

      if (hour.valueOf() < firstTimestamp) {
        hour.add(1, "hour");
      }

      const hourlyTicks: number[] = [];
      while (hour.valueOf() <= lastTimestamp) {
        hourlyTicks.push(hour.valueOf());
        hour.add(1, "hour");
      }

      return hourlyTicks.length > 0 ? hourlyTicks : [firstTimestamp];
    }

    const day = moment(firstTimestamp).tz(effectiveTimezone).startOf("day");

    if (day.valueOf() < firstTimestamp) {
      day.add(1, "day");
    }

    const dailyTicks: number[] = [];
    while (day.valueOf() <= lastTimestamp) {
      dailyTicks.push(day.valueOf());
      day.add(1, "day");
    }

    if (dailyTicks.length === 0) {
      return [firstTimestamp];
    }

    const maximumTickCount = 5;
    if (dailyTicks.length <= maximumTickCount) {
      return dailyTicks;
    }

    return Array.from({ length: maximumTickCount }, (_, index) => {
      const tickIndex = Math.round((index * (dailyTicks.length - 1)) / (maximumTickCount - 1));
      return dailyTicks[tickIndex];
    });
  }, [chartData, days, effectiveTimezone]);

  const handleTimeFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setTimeFilterAnchor(event.currentTarget);
  };

  const handleTimeFilterClose = () => {
    setTimeFilterAnchor(null);
  };

  const handleDaysChange = (newDays: number) => {
    setDays(newDays);
    handleTimeFilterClose();
  };

  const getLastUpdatedAt = () => {
    if (device?.updatedAt) {
      return formatStoreDateTimeWithTimezone(device.updatedAt, effectiveTimezone, locale);
    }
    return t("management.devices.devices.empty.notAvailable");
  };

  const timeFilterOptions = [1, 3, 7, 14, 30, 60, 90, 180];

  const formatChartTimestamp = (timestamp: number) => {
    const date = moment(timestamp).tz(effectiveTimezone);

    return date.format(days === 1 ? "HH:mm" : days > 3 ? "MMM D" : "MMM D, HH:mm");
  };

  const formatTooltipTimestamp = (timestamp: number) =>
    formatStoreDateTimeWithTimezone(timestamp, effectiveTimezone, locale);

  const PingChartTooltip: React.FC<PingChartTooltipProps> = ({ active, coordinate, payload }) => {
    const status = Number(payload?.[0]?.value);
    const point = payload?.[0]?.payload;

    if (!active || !point || !Number.isFinite(status)) {
      return null;
    }

    return (
      <ChartHoverTooltip
        active={active}
        coordinate={coordinate}
        containerRef={pingChartContainerRef}
        height={76}
      >
        <Box sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>
          {formatTooltipTimestamp(point.timestamp)}
        </Box>
        <Box
          sx={{
            mt: 0.75,
            color: status === 1 ? "#6EE7B7" : "#FDA4AF",
            fontSize: 11,
            lineHeight: 1.35,
          }}
        >
          {t("management.devices.devices.fields.status")} ·{" "}
          {status === 1
            ? t("management.devices.devices.status.online")
            : t("management.devices.devices.status.offline")}
        </Box>
      </ChartHoverTooltip>
    );
  };

  const getStatusChipColor = (enabled?: boolean) => {
    return enabled ? "success" : "error";
  };

  const getPlatformIconSource = (operatingSystemType?: string) => {
    switch (operatingSystemType?.toUpperCase()) {
      case "ANDROID":
        return "/images/icons/android_icon.svg";
      case "IOS":
        return "/images/icons/ios_icon.svg";
      default:
        return null;
    }
  };

  const renderBatteryIcon = (icon: DeviceBatteryIcon, color: DeviceBatteryColor) => {
    const sx = { fontSize: 18 };

    switch (icon) {
      case "battery0":
        return <Battery0Icon color={color} sx={sx} />;
      case "battery20":
        return <Battery20Icon color={color} sx={sx} />;
      case "battery50":
        return <Battery50Icon color={color} sx={sx} />;
      case "battery80":
        return <Battery80Icon color={color} sx={sx} />;
      case "power":
        return <PowerIcon color={color} sx={sx} />;
      default:
        return <BatteryFullIcon color={color} sx={sx} />;
    }
  };

  const handleAction = async (action: string) => {
    if (!device) return;

    try {
      setIsLoading(true);
      setError(null);

      switch (action) {
        case "restart":
          await deviceService.restartDeviceById(device.id);
          break;
        case "disable":
          await deviceService.disableDeviceById(device.id);
          await loadDeviceData();
          break;
        case "lock":
          await deviceService.lockDeviceById(device.id, true);
          await loadDeviceData();
          break;
        case "unlock":
          await deviceService.lockDeviceById(device.id, false);
          await loadDeviceData();
          break;
        case "toggleReportExclusion": {
          const response = await deviceService.setDeviceIgnoredById(device.id, !device.ignored);
          if (!response.success) {
            throw (
              response.error ??
              new Error(t("management.devices.deviceHistory.errors.errorPerformingAction"))
            );
          }
          clearCommandCenterSessionCache({
            region: getCurrentRegion(),
            userId: coreService.getStoredUser()?.id,
          });
          await loadDeviceData();
          break;
        }
        case "logs":
          window.open(buildDeviceLogsUrl(device.id, device.storeId), "_blank");
          break;
        case "pin":
          const response = await deviceService.getDevicePinById(device.id);
          if (response.success && response.body) {
            setCurrentPin(response.body);
            setPinModalOpen(true);
          } else {
            setError(t("management.devices.deviceHistory.errors.failedGetDevicePin"));
          }
          break;
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.devices.deviceHistory.errors.errorPerformingAction"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const canRestart = (device: Device) => {
    return (
      device.type &&
      device.operatingSystemType &&
      (device.type === "DOOR" || device.type === "SHOPPER") &&
      device.operatingSystemType === "IOS"
    );
  };
  const canManageAsam = Boolean(device && device.operatingSystemType?.toUpperCase() !== "ANDROID");
  const hasDeviceControlActions = Boolean(device && (canRestart(device) || canManageAsam));

  const sectionLabelSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  } as const;
  const topSummaryCardSx = { height: "100%", minHeight: 176 } as const;
  const permissionsCardSx = { minHeight: 152 } as const;

  if (isLoading && !device) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("management.devices.deviceHistory.text.title", { deviceId })}
        icon={<DeviceIcon />}
        titleAccessory={<LoadingRing loading />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ ...topSummaryCardSx, p: 2 }}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width={110} />
                  </Stack>
                  <Skeleton variant="rounded" width={76} height={24} />
                </Box>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}
                >
                  <Box>
                    <Skeleton variant="text" width="40%" height={14} />
                    <Skeleton variant="text" width="55%" />
                  </Box>
                  <Box>
                    <Skeleton variant="text" width="40%" height={14} />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                </Box>
                <Box>
                  <Skeleton variant="text" width="30%" height={14} />
                  <Skeleton variant="text" width="85%" />
                  <Skeleton variant="text" width="35%" height={14} />
                </Box>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ ...topSummaryCardSx, p: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={150} />
                </Stack>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}
                >
                  <Box>
                    <Skeleton variant="text" width="55%" height={14} />
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Skeleton variant="rounded" width={24} height={13} />
                      <Skeleton variant="text" width="55%" />
                    </Stack>
                  </Box>
                  <Box>
                    <Skeleton variant="text" width="65%" height={14} />
                    <Skeleton variant="text" width="85%" />
                  </Box>
                </Box>
                <Box>
                  <Skeleton variant="text" width="45%" height={14} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="circular" width={18} height={18} />
                    <Skeleton variant="rounded" height={4} sx={{ flexGrow: 1 }} />
                    <Skeleton variant="text" width={38} />
                  </Stack>
                </Box>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ ...topSummaryCardSx, p: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={75} />
                </Stack>
                <Box>
                  <Skeleton variant="text" width="25%" height={14} />
                  <Skeleton variant="text" width="90%" />
                </Box>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}
                >
                  <Box>
                    <Skeleton variant="text" width="45%" height={14} />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                  <Box>
                    <Skeleton variant="text" width="45%" height={14} />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                </Box>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ ...permissionsCardSx, p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={230} />
              </Stack>
              <Grid container spacing={3}>
                {Array.from({ length: 4 }, (_, index) => (
                  <Grid key={index} item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Skeleton variant="text" width="55%" height={14} />
                          <Skeleton variant="text" width="70%" />
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.devices.deviceHistory.text.title", { deviceId })}
      icon={<DeviceIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
    >
      <Box sx={{ mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <AdminSectionPanel
              title={t("management.devices.deviceHistory.text.deviceInfo")}
              icon={<DeviceIcon />}
              actions={
                <Chip
                  icon={
                    device?.enabled ? (
                      <EnabledIcon sx={{ fontSize: 16, color: "success.main" }} />
                    ) : (
                      <DisabledStatusIcon sx={{ fontSize: 16, color: "grey.500" }} />
                    )
                  }
                  label={
                    device?.enabled
                      ? t("management.devices.devices.status.enabled")
                      : t("management.devices.devices.status.disabled")
                  }
                  color={getStatusChipColor(device?.enabled)}
                  size="small"
                />
              }
              paperSx={topSummaryCardSx}
            >
              <Stack spacing={2}>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                      {t("management.devices.devices.fields.id")}
                    </Typography>
                    <Typography variant="body1">
                      {device?.id ?? t("management.devices.devices.empty.notAvailable")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                      {t("management.devices.devices.fields.name")}
                    </Typography>
                    <Typography variant="body1">
                      {device?.name || t("management.devices.devices.empty.notAvailable")}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                    {t("management.devices.deviceHistory.text.lastUpdate")}
                  </Typography>
                  <Typography variant="body1">{getLastUpdatedAt()}</Typography>
                  {device?.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeStoreTime(device.updatedAt, effectiveTimezone, locale)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </AdminSectionPanel>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AdminSectionPanel
              title={t("management.devices.deviceHistory.text.systemAppInfo")}
              icon={<SystemIcon />}
              paperSx={topSummaryCardSx}
            >
              <Stack spacing={2}>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                      {t("management.devices.deviceHistory.text.osVersion")}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {getPlatformIconSource(device?.operatingSystemType) && (
                        <Box
                          component="img"
                          src={getPlatformIconSource(device?.operatingSystemType)!}
                          alt=""
                          aria-hidden
                          sx={{ width: 24, height: 13, objectFit: "contain", flexShrink: 0 }}
                        />
                      )}
                      <Typography variant="body1">
                        {device?.deviceVersion ||
                          t("management.devices.devices.empty.notAvailable")}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                      {t("management.devices.deviceHistory.text.appVersion")}
                    </Typography>
                    <Typography variant="body1">{device?.appVersion}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                    {t("management.devices.deviceHistory.text.batteryLevel")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {batteryDisplay && device?.batteryPercent !== undefined ? (
                      <>
                        {renderBatteryIcon(batteryDisplay.icon, batteryDisplay.color)}
                        {!batteryDisplay.isPluggedIn && (
                          <LinearProgress
                            variant="determinate"
                            value={device.batteryPercent}
                            color={batteryDisplay.color}
                            sx={{ flexGrow: 1 }}
                          />
                        )}
                        <Typography variant="body1">
                          {batteryDisplay.isPluggedIn
                            ? t("commandCenter.status.wallPower")
                            : formatPercentFromWhole(device.batteryPercent, locale)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1">{t("common.notAvailable")}</Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </AdminSectionPanel>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AdminSectionPanel
              title={t("management.devices.deviceHistory.text.location")}
              icon={<StoreIcon />}
              actions={
                <Chip
                  icon={
                    device?.ignored ? (
                      <ExcludedFromReportsStatusIcon />
                    ) : (
                      <IncludedInReportsStatusIcon />
                    )
                  }
                  label={
                    device?.ignored
                      ? t("management.devices.deviceHistory.text.excludedFromReports")
                      : t("management.devices.deviceHistory.text.includedInReports")
                  }
                  size="small"
                  sx={(theme) => {
                    const color = device?.ignored
                      ? theme.palette.grey[600]
                      : theme.palette.primary.main;

                    return {
                      alignSelf: { xs: "flex-end", sm: "auto" },
                      backgroundColor: alpha(color, 0.1),
                      border: `1px solid ${alpha(color, 0.4)}`,
                      color,
                      "& .MuiChip-icon": { color },
                    };
                  }}
                  variant="outlined"
                />
              }
              paperSx={topSummaryCardSx}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                    {t("management.devices.devices.fields.store")}
                  </Typography>
                  <Typography variant="body1">
                    {store
                      ? formatStoreSelectorLabel(store, t, user?.role)
                      : t("management.devices.devices.empty.notAvailable")}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                      {t("management.devices.deviceHistory.text.area")}
                    </Typography>
                    <Typography variant="body1">
                      {areaName || t("management.devices.devices.empty.notAvailable")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                      {t("management.devices.deviceHistory.text.roomAssignment")}
                    </Typography>
                    <Typography variant="body1">
                      {roomName || t("management.devices.devices.empty.notAvailable")}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </AdminSectionPanel>
          </Grid>

          {/* Device Actions Section */}
          <Grid item xs={12} sx={{ order: 2 }}>
            <AdminSectionPanel
              title={t("management.devices.deviceHistory.text.deviceActions")}
              icon={<SettingsIcon />}
              headerSx={{ mb: 3 }}
            >
              <Grid container spacing={2}>
                {device && canRestart(device) && (
                  <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                      onClick={() => void handleAction("restart")}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <RestartIcon color="warning" />
                        <Box>
                          <Typography variant="subtitle1">
                            {t("management.devices.deviceHistory.text.restartApp")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("management.devices.deviceHistory.text.restartDeviceApplication")}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                )}
                {canManageAsam && (
                  <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                      onClick={() =>
                        void handleAction(device?.singleAppModeEnabled ? "unlock" : "lock")
                      }
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {device?.singleAppModeEnabled ? (
                          <LockOpenIcon color="warning" />
                        ) : (
                          <LockIcon color="success" />
                        )}
                        <Box>
                          <Typography variant="subtitle1">
                            {device?.singleAppModeEnabled
                              ? t("management.devices.deviceHistory.text.disableAsam")
                              : t("management.devices.deviceHistory.text.enableAsam")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {device?.singleAppModeEnabled
                              ? t("management.devices.deviceHistory.text.takeOutOfAsam")
                              : t("management.devices.deviceHistory.text.putInAsam")}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                )}
                {hasDeviceControlActions && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
                {device?.enabled && (
                  <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                      onClick={() => void handleAction("disable")}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <DisableIcon color="error" />
                        <Box>
                          <Typography variant="subtitle1">
                            {t("management.devices.deviceHistory.text.disableDevice")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("management.devices.deviceHistory.text.disableThisDevice")}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => void handleAction("toggleReportExclusion")}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      {device?.ignored ? (
                        <IncludeInReportsIcon color="primary" />
                      ) : (
                        <ExcludeFromReportsIcon color="action" />
                      )}
                      <Box>
                        <Typography variant="subtitle1">
                          {device?.ignored
                            ? t("management.devices.deviceHistory.text.includeInReports")
                            : t("management.devices.deviceHistory.text.excludeFromReports")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {device?.ignored
                            ? t("management.devices.deviceHistory.text.includeInReportsDescription")
                            : t(
                                "management.devices.deviceHistory.text.excludeFromReportsDescription",
                              )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => void handleAction("logs")}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <LogsIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle1">
                          {t("management.devices.deviceHistory.text.viewLogs")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("management.devices.deviceHistory.text.accessDeviceLogsMezmo")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => void handleAction("pin")}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <PinIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle1">
                          {t("management.devices.deviceHistory.text.viewPin")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("management.devices.deviceHistory.text.showDevicePinCode")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </AdminSectionPanel>
          </Grid>

          {/* Device Permissions Section */}
          <Grid item xs={12} sx={{ order: 1 }}>
            <AdminSectionPanel
              title={t("management.devices.deviceHistory.text.devicePermissionsSettings")}
              icon={<PhonelinkLock />}
              headerSx={{ mb: 3 }}
              paperSx={permissionsCardSx}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {device?.allowedCamera ? (
                        <CameraIcon color="success" />
                      ) : (
                        <CameraOffIcon color="error" />
                      )}
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                          {t("management.devices.deviceHistory.text.camera")}
                        </Typography>
                        <Typography>
                          {device?.allowedCamera
                            ? t("management.devices.deviceHistory.text.granted")
                            : t("management.devices.deviceHistory.text.notGranted")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {device?.allowedMicrophone ? (
                        <MicIcon color="success" />
                      ) : (
                        <MicOffIcon color="error" />
                      )}
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                          {t("management.devices.deviceHistory.text.microphone")}
                        </Typography>
                        <Typography>
                          {device?.allowedMicrophone
                            ? t("management.devices.deviceHistory.text.granted")
                            : t("management.devices.deviceHistory.text.notGranted")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {device?.allowedNotifications ? (
                        <NotificationsIcon color="success" />
                      ) : (
                        <NotificationsOffIcon color="error" />
                      )}
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                          {t("management.devices.deviceHistory.text.notifications")}
                        </Typography>
                        <Typography>
                          {device?.allowedNotifications
                            ? t("management.devices.deviceHistory.text.granted")
                            : t("management.devices.deviceHistory.text.notGranted")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {device?.enableSingleAppMode ? (
                        <LockIcon color="success" />
                      ) : (
                        <LockOpenIcon color="error" />
                      )}
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                          {t("management.devices.deviceHistory.text.autonomousSingleAppMode")}
                        </Typography>
                        <Typography>
                          {device?.enableSingleAppMode
                            ? t("management.devices.devices.status.enabled")
                            : t("management.devices.devices.status.disabled")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </AdminSectionPanel>
          </Grid>

          {(isMetadataLoading || latestMetadata) && (
            <Grid item xs={12} sx={{ order: 5 }}>
              <AdminSectionPanel
                title={t("management.devices.deviceHistory.text.diagnostics")}
                icon={<LogsIcon />}
                headerSx={{ mb: 3 }}
              >
                {isMetadataLoading ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {[0, 1, 2].map((index) => (
                      <Box key={index}>
                        <Skeleton variant="text" width="40%" height={14} />
                        <Skeleton variant="text" width="80%" />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {latestMetadata &&
                      Object.entries(latestMetadata.metadata)
                        .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
                        .map(([key, value]) => (
                          <Box key={key}>
                            <Typography variant="body2" color="text.secondary" sx={sectionLabelSx}>
                              {formatMetadataKey(key)}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        ))}
                  </Box>
                )}
              </AdminSectionPanel>
            </Grid>
          )}

          {/* Device History Chart */}
          <Grid item xs={12} sx={{ order: 4 }}>
            <AdminSectionPanel
              title={t("management.devices.deviceHistory.text.devicePingHistory")}
              icon={<TimeIcon />}
              headerSx={{ mb: 3 }}
              actions={
                <Button
                  variant="outlined"
                  onClick={handleTimeFilterClick}
                  endIcon={<KeyboardArrowDownIcon />}
                  size="small"
                >
                  {t("management.devices.deviceHistory.text.previous", {
                    value:
                      days === 1
                        ? t("management.devices.deviceHistory.text.day")
                        : t("management.devices.deviceHistory.text.days"),
                    days,
                  })}
                </Button>
              }
            >
              {!isHistoryLoading && pings.length === 0 ? (
                <AdminEmptyState
                  icon={<TimeIcon sx={{ fontSize: 40 }} />}
                  text={t("management.devices.deviceHistory.text.noDataAvailable")}
                />
              ) : (
                <Box ref={pingChartContainerRef} sx={{ position: "relative", height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                      <defs>
                        <linearGradient
                          id="device-ping-status-gradient"
                          gradientUnits="userSpaceOnUse"
                          x1="0"
                          y1="100%"
                          x2="0"
                          y2="0%"
                        >
                          <stop offset="0%" stopColor="#f44336" />
                          <stop offset="100%" stopColor="#4CAF50" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#e5e7eb" />
                      <XAxis
                        type="number"
                        dataKey="timestamp"
                        domain={["dataMin", "dataMax"]}
                        scale="time"
                        tickFormatter={formatChartTimestamp}
                        tick={{ fontSize: 12 }}
                        ticks={xAxisTicks}
                      />
                      <YAxis
                        type="number"
                        dataKey="status"
                        domain={[-0.1, 1.1]}
                        ticks={[0, 1]}
                        tickFormatter={(value: number) =>
                          value === 1
                            ? t("management.devices.devices.status.online")
                            : t("management.devices.devices.status.offline")
                        }
                        tick={{ fontSize: 12 }}
                      />
                      <RechartsTooltip
                        cursor={{ stroke: "#C9D6E5", strokeWidth: 1 }}
                        content={<PingChartTooltip />}
                        offset={0}
                        isAnimationActive={false}
                      />
                      <Line
                        type="stepAfter"
                        dataKey="status"
                        stroke="url(#device-ping-status-gradient)"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {isHistoryLoading && (
                    <Box
                      role="status"
                      aria-live="polite"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        bgcolor: "rgba(255, 255, 255, 0.72)",
                        color: "text.secondary",
                      }}
                    >
                      <CircularProgress size={28} />
                      {t("common.loading")}
                    </Box>
                  )}
                </Box>
              )}
              <Menu
                anchorEl={timeFilterAnchor}
                open={Boolean(timeFilterAnchor)}
                onClose={handleTimeFilterClose}
              >
                {timeFilterOptions.map((option) => (
                  <MenuItem
                    key={option}
                    onClick={() => handleDaysChange(option)}
                    selected={days === option}
                  >
                    {t("management.devices.deviceHistory.text.previous", {
                      value:
                        option === 1
                          ? t("management.devices.deviceHistory.text.day")
                          : t("management.devices.deviceHistory.text.days"),
                      days: option,
                    })}
                  </MenuItem>
                ))}
              </Menu>
            </AdminSectionPanel>
          </Grid>
        </Grid>
      </Box>

      {/* PIN Dialog */}
      <AdminDialog
        open={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        maxWidth="sm"
        title={t("management.devices.deviceHistory.text.devicePin")}
        icon={<PinIcon />}
      >
        <Typography variant="h4" sx={{ textAlign: "center", fontFamily: "monospace" }}>
          {currentPin}
        </Typography>
      </AdminDialog>
    </PageShell>
  );
};

export default DeviceHistory;
